import Icon from '@components/v2/ui/Icon/Icon';
import Link from '@components/v2/ui/Link/Link';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Theme, alpha } from '@mui/material';
import { FileUIPart } from 'ai';
import { getRachelFileIcon } from '../../../../rachel.files.presentation';
import {
  getRachelFileDisplayName,
  isRachelFileThumbnail,
  isSafeUrl,
} from './rachelFilePartView.utils';

interface RachelFilePartViewProps {
  part: FileUIPart;
}

const THUMBNAIL_SIZE = 64;

const CHIP_HOVER_BACKGROUND_OPACITY = 0.05;

const getChipSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  gap: 0.5,
  pl: 0.75,
  pr: 1,
  py: 0.5,
  maxWidth: '100%',
  borderRadius: `${theme.shape.borderRadiusLg}px`,
  border: `0.5px solid ${theme.palette.custom.border.secondary}`,
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.primary,
  '& > svg': { flexShrink: 0 },
});

const getLinkChipSx = (theme: Theme) => ({
  ...getChipSx(theme),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, CHIP_HOVER_BACKGROUND_OPACITY),
  },
});

const getThumbnailSx = (theme: Theme) => ({
  display: 'block',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  overflow: 'hidden',
  borderRadius: `${theme.shape.borderRadiusLg}px`,
  border: `0.5px solid ${theme.palette.custom.border.primary}`,
});

const RachelFilePartView = ({ part }: RachelFilePartViewProps) => {
  const displayName = getRachelFileDisplayName(part);

  if (isRachelFileThumbnail(part)) {
    return (
      <Link href={part.url} underline="none" sx={getThumbnailSx}>
        <Box
          component="img"
          src={part.url}
          alt={displayName}
          sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Link>
    );
  }

  const chipContent = (
    <>
      <Icon icon={getRachelFileIcon(part.mediaType)} size="md" />
      <Typography variant="body2" noWrap title={displayName}>
        {displayName}
      </Typography>
    </>
  );

  if (!isSafeUrl(part.url)) {
    return <Box sx={getChipSx}>{chipContent}</Box>;
  }

  return (
    <Link href={part.url} underline="none" color="text.primary" sx={getLinkChipSx}>
      {chipContent}
    </Link>
  );
};

export default RachelFilePartView;
